import { type DocumentType, plugin } from "@typegoose/typegoose";
import mongooseLeanDefaultsImport from "mongoose-lean-defaults";
import mongooseLeanVirtuals from "mongoose-lean-virtuals";
import type { VerdiSchemaKey } from "./index.js";
import type { ValidatableClass } from "./validatableClass.js";
import { DtoValidatorTypes, DtoValidatorTypesBase } from "./validatableClass.js";

// mongoose-lean-defaults is a CJS module that uses `exports.default = fn`.
// When bundled or transpiled the default import may be the full exports object;
// unwrap it if necessary.
const mongooseLeanDefaults =
    typeof (mongooseLeanDefaultsImport as any).default === "function"
        ? (mongooseLeanDefaultsImport as any).default
        : mongooseLeanDefaultsImport;

/**
 * Type guard to check if an object has a toJSON method
 */
function hasToJSON(obj: unknown): obj is { toJSON: () => any } {
    return obj != null && typeof obj === "object" && "toJSON" in obj && typeof obj.toJSON === "function";
}

// Register lean plugins on SchemaBase so every schema inherits them automatically.
// This ensures `.lean({ virtuals: true, defaults: true })` resolves schema virtuals
// (e.g. schemaKey) and defaults on all models without per-schema registration.
@plugin(mongooseLeanDefaults)
@plugin(mongooseLeanVirtuals)
export abstract class SchemaBase<
    ImplementingSchema extends SchemaBase<any>,
    FieldsWithDefaults extends string = never,
> {
    static schemaKey: string = undefined;

    static getters: Record<string, (instance: any) => any> = {};

    static utilities: Record<string, (...args: any[]) => any> = {};

    static dtoValidators?: DtoValidatorTypes<DtoValidatorTypesBase>;

    /**
     * The schema type must also be the collection name, if the schema is used in a collection
     */
    readonly schemaKey: string;

    /**
     * Similar to the mongo function to make a new instance, this allows you to set any properties you like in the constructor
     * It also populates the schemaKey appropriately.
     *
     * It only knows about fields with defaults if those fields are specified in the second type parameter of SchemaBase as a
     * string union. This needs a bit more thought.
     * @param properties
     */
    constructor(properties?: SchemaConstructorArgs<ImplementingSchema, FieldsWithDefaults>) {
        /**
         * The logic here is that we want to assign the properties to the object we are constructing, but if the
         * properties were passed in as a Document, then getters and setters might play a role. This means
         * Object.assign(this, properties) may not get us a complete model. We therefore assign properties.toJSON()
         * as well, if it exists.
         *
         * We will possibly end up with more properties being assigned than we might want, but we will make sure we
         * are always getting what we need.
         *
         * If we remove the original object.assign, that should work unless somehow .toJSON finds something else named
         * the same thing that doesn't behave the same way. That could be a problem.
         */
        Object.assign(this, properties);

        if (hasToJSON(properties)) {
            try {
                Object.assign(this, properties.toJSON());
            } catch (e) {
                console.warn(
                    "Error calling .toJSON with no arguments on object passed into schema constructor, despite that being a function",
                );
            }
        }
        // @ts-ignore
        this.schemaKey = this.constructor.schemaKey;
    }
}

/** Compile-time check: `SchemaBase` constructor exposes the optional static `dtoValidators` slot. */
void (SchemaBase satisfies ValidatableClass);

/**
 * Plain object acceptable to the constructor: required data keys plus FieldsWithDefaults as optional.
 */
type SchemaConstructorPartial<S extends SchemaBase<any>, DefaultKeys extends string = never> = Pick<
    S,
    Exclude<Exclude<keyof S, keyof SchemaBase<any>>, DefaultKeys>
> &
    Partial<Pick<S, Extract<DefaultKeys, keyof S>>>;

export type SchemaConstructorArgs<
    ImplementingSchema extends SchemaBase<any>,
    FieldsWithDefaults extends string = never,
> =
    | SchemaConstructorPartial<ImplementingSchema, FieldsWithDefaults>
    | ImplementingSchema
    | (DocumentType<ImplementingSchema> & { schemaKey?: ImplementingSchema["schemaKey"] });

/**
 * Adds the schema key to a model when it is found in the db. Should be present on all schemas with mongo db metadata
 * @param schemaKey
 * @returns {[[string, string, string], (this:DocumentType<SchemaBase>, doc: DocumentType<SchemaBase>) => void]}
 */
export const addSchemaKeyHook = (
    schemaKey: VerdiSchemaKey,
): [
    ["find", "findOne", "findOneAndUpdate"],
    (this: DocumentType<SchemaBase<any>>, doc: DocumentType<SchemaBase<any>>) => void,
] => [
    ["find", "findOne", "findOneAndUpdate"],
    function (this: DocumentType<SchemaBase<any>>, doc: DocumentType<SchemaBase<any>>) {
        if (doc) {
            // Store the current sensorParameters as a string for later comparison
            // @ts-ignore
            doc["schemaKey"] = schemaKey;
        }
    },
];

export const getDefaultSchemaOptions = (schemaKey: string) => ({
    _id: false,
    toJSON: { virtuals: true },
    toObject: { virtuals: true },
    virtuals: {
        schemaKey: {
            get: () => schemaKey,
        },
    },
    minimize: false,
});
